import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "state-manager/store";

interface WorkerResponse<T> {
  success: boolean;
  data?: T | null;
  error?: string;
}

interface WorkerMessage {
  url: string;
  interval: number;
  token: string;
}

export const usePollingWorker = <T>(url: string, interval: number) => {
  const token = useSelector((state: RootState) => state.profile.token);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    workerRef.current = new Worker(
      new URL("../workers/polling-worker.js", import.meta.url)
    );
    workerRef.current.postMessage({
      url: `${process.env.REACT_APP_SERVER_API}${url}`,
      interval,
      token,
    } as WorkerMessage);

    workerRef.current.onmessage = (event: MessageEvent<WorkerResponse<T>>) => {
      const { success, data, error } = event.data;
      setLoading(false);
      if (success) {
        setData(data ?? null);
        setError(null);
      } else {
        setError(error || "Unknown error");
      }
    };

    return () => {
      workerRef.current?.postMessage("stop");
      workerRef.current?.terminate();
    };
  }, [url, interval, token]);

  return { data, loading, error };
};
